import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import './FileInput.css';

/**
 * 파일 크기를 읽기 쉬운 형식으로 변환
 * @param {number} bytes - 바이트 크기
 * @returns {string} - 포맷된 크기 (예: "1.5 MB")
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * FileInput 컴포넌트
 * @param {Object} props
 * @param {File | File[] | null} props.value - 선택된 파일 (단일 또는 배열)
 * @param {Function} props.onChange - 파일 변경 시 호출 (File 또는 File[] 전달)
 * @param {boolean} props.multiple - 다중 파일 선택 허용 여부
 * @param {string} props.accept - 허용할 파일 타입 (예: "image/*", ".pdf,.doc")
 * @param {number} props.maxSize - 최대 파일 크기 (bytes)
 * @param {string} props.placeholder - 플레이스홀더 텍스트
 * @param {boolean} props.disabled - 비활성화 여부
 * @param {string} props.className - 추가 CSS 클래스
 * @param {boolean} props.showPreview - 이미지 미리보기 표시 여부
 * @param {boolean} props.dragDrop - 드래그 앤 드롭 지원 여부
 * @param {string} props.errorMessage - 에러 메시지
 */
const FileInput = ({
  value = null,
  onChange = () => {},
  multiple = false,
  accept = '',
  maxSize = null, // bytes
  placeholder = '파일을 선택하세요',
  disabled = false,
  className = '',
  showPreview = false,
  dragDrop = true,
  errorMessage = '',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // 파일 배열로 정규화
  const files = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // 파일 선택 처리
  const handleFileSelect = useCallback((selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileArray = Array.from(selectedFiles);
    const validFiles = [];
    const errors = [];

    fileArray.forEach((file) => {
      // 파일 크기 검증
      if (maxSize && file.size > maxSize) {
        errors.push(`${file.name}: 파일 크기가 ${formatFileSize(maxSize)}를 초과합니다.`);
        return;
      }

      // 파일 타입 검증 (accept가 있는 경우)
      if (accept) {
        const acceptTypes = accept.split(',').map((type) => type.trim());
        const isValidType = acceptTypes.some((type) => {
          if (type.startsWith('.')) {
            // 확장자로 검증 (예: .pdf, .jpg)
            return file.name.toLowerCase().endsWith(type.toLowerCase());
          } else if (type.includes('/*')) {
            // MIME 타입으로 검증 (예: image/*, application/*)
            const baseType = type.split('/')[0];
            return file.type.startsWith(baseType + '/');
          } else {
            // 정확한 MIME 타입으로 검증
            return file.type === type;
          }
        });

        if (!isValidType) {
          errors.push(`${file.name}: 허용되지 않은 파일 형식입니다.`);
          return;
        }
      }

      validFiles.push(file);
    });

    // 에러가 있으면 표시
    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setError('');
    
    // 파일 설정
    if (multiple) {
      // 다중 파일 모드: 기존 파일과 새 파일 합치기
      const existingFiles = Array.isArray(value) ? value : (value ? [value] : []);
      onChange([...existingFiles, ...validFiles]);
    } else {
      // 단일 파일 모드: 첫 번째 파일만
      onChange(validFiles[0] || null);
    }
  }, [value, onChange, multiple, accept, maxSize]);

  // 파일 입력 변경
  const handleInputChange = (e) => {
    const selectedFiles = e.target.files;
    handleFileSelect(selectedFiles);
    // 같은 파일을 다시 선택할 수 있도록 input 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  // 파일 버튼 클릭
  const handleButtonClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 드래그 앤 드롭
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && dragDrop) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || !dragDrop) return;

    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  // 이미지 미리보기 URL 생성 및 정리
  const previewUrlsRef = useRef({});
  const [previewUrls, setPreviewUrls] = useState({});

  useEffect(() => {
    if (!showPreview || files.length === 0) {
      // 모든 URL 정리
      Object.values(previewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      previewUrlsRef.current = {};
      setPreviewUrls({});
      return;
    }

    // 새로운 URL 생성
    const newUrls = {};
    files.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        // 기존 URL이 있으면 재사용, 없으면 새로 생성
        const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
        if (previewUrlsRef.current[fileKey]) {
          newUrls[index] = previewUrlsRef.current[fileKey];
        } else {
          newUrls[index] = URL.createObjectURL(file);
          previewUrlsRef.current[fileKey] = newUrls[index];
        }
      }
    });

    // 사용하지 않는 URL 정리
    const usedUrls = new Set(Object.values(newUrls));
    Object.entries(previewUrlsRef.current).forEach(([key, url]) => {
      if (!usedUrls.has(url)) {
        URL.revokeObjectURL(url);
        delete previewUrlsRef.current[key];
      }
    });

    setPreviewUrls(newUrls);

    // 컴포넌트 언마운트 시 정리
    return () => {
      Object.values(previewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      previewUrlsRef.current = {};
    };
  }, [files, showPreview]);

  // 파일 제거
  const handleRemoveFile = (index) => {
    if (multiple) {
      const newFiles = files.filter((_, i) => i !== index);
      onChange(newFiles.length > 0 ? newFiles : null);
    } else {
      onChange(null);
    }
    setError('');
    // useEffect에서 자동으로 URL 정리됨
  };

  return (
    <div className={`file-input-container ${className} ${disabled ? 'disabled' : ''}`}>
      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={handleInputChange}
        disabled={disabled}
        className="file-input-hidden"
      />

      {/* 파일 선택 영역 */}
      <div
        className={`file-input-dropzone ${isDragging ? 'dragging' : ''} ${files.length > 0 ? 'has-files' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        {files.length === 0 ? (
          <div className="file-input-placeholder">
            <p className="file-input-placeholder-text">{placeholder}</p>
            {dragDrop && (
              <p className="file-input-placeholder-hint">파일을 클릭하거나 드래그하여 선택하세요</p>
            )}
          </div>
        ) : (
          <div className="file-input-files">
            {files.map((file, index) => {
              const previewUrl = showPreview ? previewUrls[index] : null;
              return (
                <div key={`${file.name}-${file.size}-${index}`} className="file-input-file-item" onClick={(e)=>{e.stopPropagation();}}>
                  {previewUrl && (
                    <div className="file-input-preview">
                      <img src={previewUrl} alt={file.name} />
                    </div>
                  )}
                  <div className="file-input-file-info">
                    <p className="file-input-file-name">{file.name}</p>
                    <p className="file-input-file-size">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    className="file-input-remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(index);
                    }}
                    disabled={disabled}
                  >
                    <DeleteIcon />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 에러 메시지 */}
      {(error || errorMessage) && (
        <div className="file-input-error">
          {error || errorMessage}
        </div>
      )}

      {/* 추가 파일 선택 버튼 (다중 파일 모드에서만) */}
      {multiple && files.length > 0 && (
        <button
          type="button"
          className="file-input-add-btn"
          onClick={handleButtonClick}
          disabled={disabled}
        >
          파일 추가
        </button>
      )}
    </div>
  );
};

export default FileInput;

